import { Button, Input, makeStyles, shorthands, Spinner, tokens } from "@fluentui/react-components";
import {
  ArrowRight16Regular,
  MyLocation16Regular,
  Open16Regular,
  Search16Regular,
  ThumbDislike16Regular,
  ThumbLike16Regular,
} from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  searchAssistant,
  sendAssistantFeedback,
  type AssistantResultCard,
  type AssistantSearchResponse,
} from "./ask-api";
import { findStepElement, flashAnchor, type MissReason, type YieldHandler } from "./dom";
import type { HelpNotice } from "./HelpLauncher";
import { HelpTooltip } from "./HelpTooltip";
import { matchesRoute } from "./registry";
import type { HelpLanguage } from "./types";

/**
 * "Ask" — the question half of the page guide, as in MGA: "where do I do X?" answered from the
 * catalog of reviewed entries (`frontend/catalog/`), anywhere in the app.
 *
 * The panel renders reviewed entries verbatim. Nothing here composes an answer: an entry that
 * reads badly is a content bug in the catalog, not a model that needs a better prompt.
 *
 *  - "Not found" is shown as itself, with the nearest jobs offered only as *searches to try*.
 *  - "Take me there" navigates AND flashes the exact control — or opens the dialog the job starts
 *    in (`anchor_action: open`). A deep link that drops someone on a busy page has answered nothing.
 *  - It only offers to point at a control the destination actually renders. A job done on a deeper
 *    page (an implementation's packages, a package's employees) links to the page above it, and
 *    the card says the missing step out loud instead of offering a button that flashes nothing.
 */

/** Five characters: short enough for the shortest real questions, long enough that a stub never asks. */
const MIN_QUESTION = 5;
/** Half a second — a keystroke per search would make the miss log unreadable. */
const DEBOUNCE_MS = 500;
/** How long an ANSWERED question stays cached. A miss is never cached — see the query below. */
const ANSWER_CACHE_MS = 10 * 60 * 1000;

type UiText = Record<string, string>;

/** One block per language in HELP_LANGUAGES. English only until the Glossa app is connected. */
const UI_TEXT: Partial<Record<HelpLanguage, UiText>> & { en: UiText } = {
  en: {
    placeholder: "Ask how to do something…",
    hint: 'Plain words work best — "add an employee", "export consents".',
    thinking: "Looking…",
    takeMeThere: "Take me there",
    takeMeThereOpen: "Take me there and open it",
    takeMeToList: "Take me to the page above it",
    showMe: "Show me the control",
    openIt: "Open it",
    needs: "Needs",
    inEnglish: "shown in English",
    didYouMean: "Try one of these:",
    helpful: "Did this help?",
    thanks: "Thanks — noted.",
    empty: "Ask a question about any screen in the app.",
    missNotOnScreen: "that control is not on screen right now.",
    missBehindSurface: "that control is inside what the highlighted button opens.",
  },
};

/**
 * The step a deep link cannot take for you: the control is on a deeper page reached by choosing a
 * record, and which record is the user's decision, never the panel's. Chosen by the route that
 * renders the control (packages exist under Company profile AND under HR, so the area alone cannot
 * say which page is meant). Whole sentences per language — some languages decline the noun.
 */
const ON_DEEPER_PAGE: Partial<Record<HelpLanguage, (anchorRoute: string) => string>> & {
  en: (anchorRoute: string) => string;
} = {
  en: (anchorRoute) => {
    const depth = anchorRoute.split("/").filter((segment) => segment.startsWith(":")).length;
    if (anchorRoute.startsWith("/company-profile/") && depth >= 4) {
      return "This control is on an implementation's packages page — on Company profile, open the benefit category and then the implementation first.";
    }
    if (anchorRoute.startsWith("/company-profile/")) {
      return "This control is on a benefit category's implementations page — open the category from Company profile first.";
    }
    if (anchorRoute.startsWith("/hr/packages/")) {
      return "This control is on an implementation's packages page — open it from the Benefits view of the HR page first.";
    }
    if (anchorRoute.startsWith("/hr/employees/")) {
      return "This control is on a package's employee list — open it from the package on the HR packages page first.";
    }
    return "This control is on a record's own page — open one from the list first.";
  },
};

/**
 * Is the card's control on THIS screen already? On its own route — or, HBH's addition to MGA's
 * rule, rendered here right now by the app frame or a shared component (the "?" in the header,
 * the subheader's refresh and search). HBH has no home page for app-wide entries to deep-link
 * to, and navigating to another page to show a control that is right here answers nothing.
 */
const pointsHere = (card: AssistantResultCard, pathname: string): boolean =>
  card.anchor !== null &&
  (matchesRoute(card.anchorRoute, pathname) ||
    findStepElement(`[data-help="${card.anchor}"]`) !== null);

/**
 * Can the panel point at this card's control — here, or on the page it is about to open? Shared by
 * the card (which labels the button) and the handler (which starts the flash), so the two never
 * disagree about whether there is anything to flash.
 */
const canPointAt = (card: AssistantResultCard, pathname: string): boolean =>
  card.anchor !== null &&
  (card.anchorRoute === card.deeplink || pointsHere(card, pathname));

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    flexGrow: 1,
  },
  top: {
    flexShrink: 0,
    paddingBottom: "12px",
  },
  input: {
    width: "100%",
  },
  hint: {
    fontSize: "11.5px",
    lineHeight: 1.5,
    color: tokens.colorNeutralForeground3,
    marginTop: "6px",
  },
  muted: {
    fontSize: "12.5px",
    color: tokens.colorNeutralForeground3,
    marginTop: "8px",
  },
  card: {
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("12px"),
    ...shorthands.padding("14px"),
    marginBottom: "10px",
    backgroundColor: tokens.colorNeutralBackground1,
  },
  chipRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    columnGap: "6px",
    marginBottom: "4px",
  },
  areaChip: {
    fontSize: "10px",
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: tokens.colorBrandForeground1,
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    ...shorthands.padding("1px", "6px"),
    whiteSpace: "nowrap",
  },
  fallback: {
    fontSize: "10.5px",
    fontStyle: "italic",
    color: tokens.colorNeutralForeground3,
  },
  job: {
    fontSize: "14px",
    fontWeight: tokens.fontWeightBold,
    lineHeight: 1.3,
    color: tokens.colorNeutralForeground1,
  },
  description: {
    fontSize: "12.5px",
    lineHeight: 1.6,
    color: tokens.colorNeutralForeground2,
    marginTop: "6px",
  },
  needs: {
    fontSize: "11.5px",
    lineHeight: 1.5,
    fontStyle: "italic",
    color: tokens.colorStatusWarningForeground1,
    marginTop: "6px",
  },
  offPage: {
    fontSize: "11.5px",
    lineHeight: 1.5,
    color: tokens.colorNeutralForeground2,
    marginTop: "6px",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    columnGap: "6px",
    marginTop: "10px",
    minHeight: "30px",
  },
  cardId: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    marginLeft: "auto",
  },
  notFound: {
    fontSize: "12.5px",
    lineHeight: 1.6,
    color: tokens.colorNeutralForeground2,
    marginTop: "8px",
  },
  tryLabel: {
    fontSize: "11.5px",
    color: tokens.colorNeutralForeground3,
    marginTop: "12px",
    marginBottom: "6px",
  },
  suggestions: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "6px",
    rowGap: "6px",
  },
  feedback: {
    display: "flex",
    alignItems: "center",
    columnGap: "4px",
    marginTop: "8px",
    paddingTop: "10px",
    ...shorthands.borderTop("1px", "solid", tokens.colorNeutralStroke2),
    fontSize: "11.5px",
    color: tokens.colorNeutralForeground3,
  },
});

const ResultCard = ({
  card,
  language,
  text,
  onNavigate,
}: {
  card: AssistantResultCard;
  language: HelpLanguage;
  text: UiText;
  onNavigate: (card: AssistantResultCard) => void;
}) => {
  const styles = useStyles();
  const { pathname } = useLocation();
  // Already on the right screen: pointing at the control beats navigating to where we are.
  const onThisScreen = pathname === card.deeplink;
  // The entry says its control opens the form the job starts in, so the button says so too.
  const opens = card.anchor !== null && card.anchorAction === "open";
  // The control is on a deeper page and the user is not on one: there is a step between the deep
  // link and the job. Saying so is the whole answer; a button that flashes nothing is not.
  const offPage = card.anchor !== null && !canPointAt(card, pathname);
  // Standing where the control is already — flash it here, and do not navigate away.
  const pointHere = pointsHere(card, pathname);
  const deeperPage = ON_DEEPER_PAGE[language] ?? ON_DEEPER_PAGE.en;

  return (
    <div className={styles.card}>
      <div className={styles.chipRow}>
        <span className={styles.areaChip}>{card.area}</span>
        {card.fallback && <span className={styles.fallback}>{text.inEnglish}</span>}
      </div>

      <div className={styles.job}>{card.job}</div>
      <div className={styles.description}>{card.description}</div>

      {card.prerequisites && (
        <div className={styles.needs}>
          {text.needs}: {card.prerequisites}
        </div>
      )}

      {offPage && <div className={styles.offPage}>{deeperPage(card.anchorRoute)}</div>}

      <div className={styles.actions}>
        {pointHere ? (
          <Button
            size="small"
            appearance="outline"
            icon={opens ? <Open16Regular /> : <MyLocation16Regular />}
            onClick={() => onNavigate(card)}
          >
            {opens ? text.openIt : text.showMe}
          </Button>
        ) : /* Already on the page the deep link points at, with the control a page deeper — or
               with no control to point at at all: nowhere to navigate, nothing to flash. The
               text above is the whole answer, and a button that did neither would be the bug. */
        onThisScreen && (offPage || card.anchor === null) ? null : (
          <Button
            size="small"
            appearance="outline"
            icon={<ArrowRight16Regular />}
            iconPosition="after"
            onClick={() => onNavigate(card)}
          >
            {offPage ? text.takeMeToList : opens ? text.takeMeThereOpen : text.takeMeThere}
          </Button>
        )}
        <span className={styles.cardId}>{card.id}</span>
      </div>
    </div>
  );
};

interface HelpAskProps {
  language: HelpLanguage;
  /** Closes the drawer when the panel navigates away, or hands over to a dialog it opens. */
  onNavigated: () => void;
  /** Asks the drawer to stand aside while a control on THIS screen is being pointed at. */
  onYield?: YieldHandler;
  /**
   * Where a miss is reported. A toast, not a line on the card: pointing at a control on ANOTHER
   * screen closes this drawer, so by the time the poll gives up there is no card left to write on.
   */
  notify: (notice: HelpNotice) => void;
}

export const HelpAsk = ({ language, onNavigated, onYield, notify }: HelpAskProps) => {
  const styles = useStyles();
  const [question, setQuestion] = useState("");
  const [debounced, setDebounced] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const cancelFlash = useRef<(() => void) | null>(null);
  const text = UI_TEXT[language] ?? UI_TEXT.en;

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(question.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [question]);

  useEffect(() => setFeedbackSent(false), [debounced]);

  // The flash is deliberately NOT cancelled on unmount: navigating closes the drawer, which
  // unmounts this panel a tick after the flash is requested — cancelling there swallowed it.

  const enabled = debounced.length >= MIN_QUESTION;
  const queryKey = ["help-ask", debounced, language, pathname];
  /**
   * A repeated ANSWER is free — entries only change on deploy. A repeated MISS is not: each search
   * logs one miss, so caching a miss would hide that someone asked the same unanswerable thing
   * three times. react-query v3 has no per-result staleTime, so the cached result decides it.
   */
  const cachedMiss =
    queryClient.getQueryData<AssistantSearchResponse>(queryKey)?.matchFound === false;
  const { data, isFetching } = useQuery(
    queryKey,
    () => searchAssistant({ question: debounced, screen: pathname, language, limit: 4 }),
    {
      enabled,
      staleTime: cachedMiss ? 0 : ANSWER_CACHE_MS,
      // The app's defaults never refetch; a repeated miss must.
      refetchOnMount: true,
      retry: false,
    }
  );

  const cards = useMemo(() => (enabled ? data?.answer.entries ?? [] : []), [data, enabled]);
  const shown = enabled ? data : undefined;

  /**
   * Says out loud that the control was not pointed at — the entry's own prerequisite appended,
   * because it usually IS the missing step ("select the client first").
   */
  const reportMiss = (card: AssistantResultCard, reason: MissReason): void => {
    const line = reason === "behind-surface" ? text.missBehindSurface : text.missNotOnScreen;
    const message = [`${card.job} —`, line, card.prerequisites].filter(Boolean).join(" ");
    notify({ severity: reason === "behind-surface" ? "info" : "warning", message });
  };

  const handleNavigate = (card: AssistantResultCard): void => {
    cancelFlash.current?.();
    const activate = card.anchorAction === "open";
    // Only for a control the destination actually renders; an off-page anchor would poll a page
    // that never has it and expire in silence.
    if (card.anchor && canPointAt(card, pathname)) {
      cancelFlash.current = flashAnchor(card.anchor, {
        activate,
        surface: card.anchorSurface,
        onYield,
        // The drawer is a dialog above the page, so a form opened under it would be hidden.
        onHandover: onNavigated,
        onMissing: (reason) => reportMiss(card, reason),
      });
    }
    if (shown?.queryId) {
      // Following a link is an action, not a verdict — sent WITHOUT `helpful`.
      void sendAssistantFeedback({ queryId: shown.queryId, deeplinkFollowed: true }).catch(
        () => undefined
      );
    }
    if (pointsHere(card, pathname)) {
      // Opening a form is always a handover: the drawer would cover the dialog.
      if (activate) onNavigated();
      return;
    }
    if (pathname !== card.deeplink) {
      navigate(card.deeplink);
      onNavigated();
    }
  };

  const handleFeedback = (helpful: boolean): void => {
    if (!shown?.queryId) return;
    setFeedbackSent(true);
    void sendAssistantFeedback({ queryId: shown.queryId, helpful }).catch(() => undefined);
  };

  return (
    <div className={styles.root}>
      <div className={styles.top}>
        <Input
          className={styles.input}
          size="small"
          autoFocus
          value={question}
          onChange={(_event, value) => setQuestion(value.value)}
          contentBefore={<Search16Regular />}
          contentAfter={isFetching ? <Spinner size="extra-tiny" /> : undefined}
          maxLength={300}
          aria-label={text.placeholder}
          placeholder={text.placeholder}
          data-help="help-ask-input"
        />
        <div className={styles.hint}>{text.hint}</div>
      </div>

      <div data-help="help-ask-results">
        {!enabled && <div className={styles.muted}>{text.empty}</div>}

        {enabled && isFetching && !shown && <div className={styles.muted}>{text.thinking}</div>}

        {cards.map((card) => (
          <ResultCard
            key={card.id}
            card={card}
            language={language}
            text={text}
            onNavigate={handleNavigate}
          />
        ))}

        {shown && !shown.matchFound && (
          <div data-help="help-ask-not-found">
            <div className={styles.notFound}>{shown.notFound?.message}</div>
            {shown.notFound?.suggestions && shown.notFound.suggestions.length > 0 && (
              <>
                <div className={styles.tryLabel}>{text.didYouMean}</div>
                <div className={styles.suggestions}>
                  {shown.notFound.suggestions.map((suggestion) => (
                    <Button
                      key={suggestion}
                      size="small"
                      shape="circular"
                      appearance="outline"
                      onClick={() => setQuestion(suggestion)}
                    >
                      {suggestion}
                    </Button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {shown && cards.length > 0 && (
          <div className={styles.feedback}>
            <span>{feedbackSent ? text.thanks : text.helpful}</span>
            {!feedbackSent && (
              <>
                <HelpTooltip content="Yes">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<ThumbLike16Regular />}
                    aria-label="Answer helped"
                    onClick={() => handleFeedback(true)}
                  />
                </HelpTooltip>
                <HelpTooltip content="No">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<ThumbDislike16Regular />}
                    aria-label="Answer did not help"
                    onClick={() => handleFeedback(false)}
                  />
                </HelpTooltip>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
